// Book grid: the Excel-style landing page (column model + filter + sort).
// Pure and React-free so it is unit tested headlessly (tests/unit/bookGrid.test.ts).
//
// The grid mirrors the practice's legacy Excel "dashboard" sheet: one dense row
// per household, sorted by Call Due, with red/green compliance cells and an
// autofilter on every column. Filters are plain JSON so they persist inside a
// saved view.
import type { ComplianceRow, ComplianceState, DashboardRow } from './types.js';
import { CONSENT_LABEL } from './cadence.js';

export type BookColumnKind = 'text' | 'category' | 'money' | 'int' | 'date' | 'compliance';

export interface BookColumn {
  key: string;
  label: string;
  kind: BookColumnKind;
  /** Rotated header (the Excel sheet's narrow tick columns). */
  vertical?: boolean;
  /** Hidden until switched on in the Columns menu. */
  defaultHidden?: boolean;
  /** Highlighted column (the Excel sheet shades Cash). */
  accent?: boolean;
}

export const COMPLIANCE_COLUMNS: Array<{ key: keyof ComplianceRow; label: string }> = [
  { key: 'riskProfile', label: 'Risk Profile' },
  { key: 'factFind', label: 'Fact Find' },
  { key: 'snapshot', label: 'Snapshot' },
  { key: 'fileNote', label: 'File Note' },
  { key: 'bestInterest', label: 'Best Interest' },
  { key: 'soaSigned', label: 'SoA Signed' },
  { key: 'feeConsent', label: 'Fee Consent' },
  { key: 'updateNeeded', label: 'Update Needed' },
  { key: 'writeSoa', label: 'Write SoA' },
  { key: 'writeRoa', label: 'Write RoA' },
];

export const BASE_COLUMNS: BookColumn[] = [
  { key: 'name', label: 'Client', kind: 'text' },
  { key: 'callDue', label: 'Call Due', kind: 'date' },
  { key: 'segment', label: 'Segment', kind: 'category' },
  { key: 'tnw', label: 'TNW', kind: 'money' },
  { key: 'cash', label: 'Cash', kind: 'money', accent: true },
  { key: 'openItems', label: 'Open Items', kind: 'int' },
  { key: 'proposedTrades', label: 'Proposed Trades', kind: 'int' },
  ...COMPLIANCE_COLUMNS.map((c): BookColumn => ({ key: `c.${c.key}`, label: c.label, kind: 'compliance', vertical: true })),
  { key: 'lastFileNote', label: 'Last File Note', kind: 'date' },
  { key: 'lastAdvice', label: 'Last Advice', kind: 'date' },
  { key: 'lastSoa', label: 'Last SoA', kind: 'date' },
  { key: 'reviewDue', label: 'Review Date', kind: 'date' },
  { key: 'reviewStage', label: 'Review Case', kind: 'category' },
  { key: 'feeConsentDate', label: 'Fee Consent Date', kind: 'date' },
  { key: 'consentStatus', label: 'Consent Renewal', kind: 'category' },
  { key: 'consentDeadline', label: 'Consent Deadline', kind: 'date' },
  { key: 'sellsCount', label: 'Sells', kind: 'int' },
  { key: 'nextBirthday', label: 'Birthday', kind: 'date' },
  { key: 'riskProfile', label: 'Risk Profile Name', kind: 'category', defaultHidden: true },
  { key: 'lastRoa', label: 'Last RoA', kind: 'date', defaultHidden: true },
];

/** Upper-case, trimmed, de-duplicated ASX-style codes; junk dropped; max 8. */
export function normaliseWatchCodes(input: string[] | string | null | undefined): string[] {
  const raw = Array.isArray(input) ? input : (input ?? '').split(',');
  const out: string[] = [];
  for (const r of raw) {
    const c = String(r).trim().toUpperCase();
    if (/^[A-Z0-9][A-Z0-9.]{0,11}$/.test(c) && !out.includes(c)) out.push(c);
    if (out.length >= 8) break;
  }
  return out;
}

/** Three columns per watched security: holding value, unrealised P/L, last trade. */
export function watchColumns(codes: string[]): BookColumn[] {
  return codes.flatMap((code): BookColumn[] => [
    { key: `w:${code}:value`, label: `${code} $`, kind: 'money' },
    { key: `w:${code}:gain`, label: `${code} P/L`, kind: 'money' },
    { key: `w:${code}:lastTrade`, label: `${code} Last Trade`, kind: 'date' },
  ]);
}

export function allColumns(watch: string[]): BookColumn[] {
  return [...BASE_COLUMNS, ...watchColumns(watch)];
}

/** ISO date (yyyy-mm-dd) in the practice timezone. */
export function todayBrisbane(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane' }).format(now);
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Next occurrence (today or later) of a date-of-birth's day/month. 29 Feb → 28 Feb in non-leap years. */
export function nextBirthday(dob: string | null | undefined, today: string): string | null {
  if (!dob || !/^\d{4}-\d{2}-\d{2}/.test(dob)) return null;
  const [, m, d] = dob.slice(0, 10).split('-').map(Number);
  const year = Number(today.slice(0, 4));
  const make = (y: number) => {
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    const day = m === 2 && d === 29 && !leap ? 28 : d;
    return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  const thisYear = make(year);
  return thisYear >= today ? thisYear : make(year + 1);
}

export function countOpenItems(c: ComplianceRow): number {
  return Object.values(c).filter((v) => v === 'outstanding').length;
}

export type CellValue = string | number | null;

/** The primitive a column sorts and filters on. Dates are ISO strings (sortable). */
export function cellValue(row: DashboardRow, key: string, today: string): CellValue {
  if (key.startsWith('c.')) return row.compliance[key.slice(2) as keyof ComplianceRow] ?? 'unknown';
  if (key.startsWith('w:')) {
    // Codes may contain dots (VAS.AX), so watch keys use ':' as `w:<CODE>:<field>`.
    const [, code, field] = key.split(':');
    const w = row.watch?.[code];
    if (!w) return null;
    if (field === 'value') return w.value;
    if (field === 'gain') return w.gain;
    if (field === 'lastTrade') return w.lastTrade;
    return null;
  }
  switch (key) {
    case 'name': return row.sortName ?? row.name;
    case 'openItems': return countOpenItems(row.compliance);
    case 'nextBirthday': return nextBirthday(row.dateOfBirth, today);
    // Display labels so the value filter reads naturally; blanks for 'none'.
    case 'consentStatus': return row.consentStatus && row.consentStatus !== 'none' ? CONSENT_LABEL[row.consentStatus] : null;
    default: {
      const v = (row as unknown as Record<string, unknown>)[key];
      if (v == null || v === '') return null;
      if (typeof v === 'number' || typeof v === 'string') return v;
      if (typeof v === 'boolean') return v ? 1 : 0;
      return String(v);
    }
  }
}

/* ---------------------------------------------------------------------------
 * Filters (Excel autofilter equivalents)
 * -------------------------------------------------------------------------*/
export const BLANK = '(Blanks)';

export type DatePreset = 'dueBy7' | 'overdue' | 'next7' | 'next30' | 'next90' | 'past90' | 'olderThan365' | 'blank' | 'notBlank';

export const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: 'dueBy7', label: 'Due in 7 days or overdue' },
  { id: 'overdue', label: 'Before today' },
  { id: 'next7', label: 'Next 7 days' },
  { id: 'next30', label: 'Next 30 days' },
  { id: 'next90', label: 'Next 90 days' },
  { id: 'past90', label: 'Last 90 days' },
  { id: 'olderThan365', label: 'Over a year ago' },
  { id: 'blank', label: 'Blank' },
  { id: 'notBlank', label: 'Not blank' },
];

export type ColumnFilter =
  | { type: 'values'; values: string[] }
  | { type: 'text'; contains: string }
  | { type: 'range'; min?: number | null; max?: number | null }
  | { type: 'date'; preset?: DatePreset | null; from?: string | null; to?: string | null };

export type FilterMap = Record<string, ColumnFilter>;

/** Label used by the values-filter list for a raw cell value. */
export function valueLabel(v: CellValue): string {
  if (v == null || v === '') return BLANK;
  return String(v);
}

export function isFilterActive(f: ColumnFilter | undefined): boolean {
  if (!f) return false;
  switch (f.type) {
    case 'values': return true; // an explicit include-list is always a restriction
    case 'text': return f.contains.trim() !== '';
    case 'range': return f.min != null || f.max != null;
    case 'date': return !!f.preset || !!f.from || !!f.to;
  }
}

export function matchesFilter(v: CellValue, f: ColumnFilter, today: string): boolean {
  switch (f.type) {
    case 'values':
      return f.values.includes(valueLabel(v));
    case 'text': {
      const needle = f.contains.trim().toLowerCase();
      return !needle || (v != null && String(v).toLowerCase().includes(needle));
    }
    case 'range': {
      if (f.min == null && f.max == null) return true;
      if (typeof v !== 'number') return false;
      if (f.min != null && v < f.min) return false;
      if (f.max != null && v > f.max) return false;
      return true;
    }
    case 'date': {
      const d = typeof v === 'string' ? v.slice(0, 10) : null;
      if (f.preset) {
        switch (f.preset) {
          case 'blank': if (d) return false; break;
          case 'notBlank': if (!d) return false; break;
          case 'dueBy7': if (!d || d > addDaysIso(today, 7)) return false; break;
          case 'overdue': if (!d || d >= today) return false; break;
          case 'next7': if (!d || d < today || d > addDaysIso(today, 7)) return false; break;
          case 'next30': if (!d || d < today || d > addDaysIso(today, 30)) return false; break;
          case 'next90': if (!d || d < today || d > addDaysIso(today, 90)) return false; break;
          case 'past90': if (!d || d > today || d < addDaysIso(today, -90)) return false; break;
          case 'olderThan365': if (!d || d >= addDaysIso(today, -365)) return false; break;
        }
      }
      if (f.from && (!d || d < f.from)) return false;
      if (f.to && (!d || d > f.to)) return false;
      return true;
    }
  }
}

export interface SortSpec { key: string; dir: 'asc' | 'desc' }

/** Nulls always sort last (either direction), like the Excel sheet's blank rows. */
export function compareCells(a: CellValue, b: CellValue, dir: 'asc' | 'desc'): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const m = dir === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * m;
  return String(a).localeCompare(String(b), 'en-AU', { sensitivity: 'base', numeric: true }) * m;
}

export interface BookQuery {
  q?: string;
  filters?: FilterMap;
  sort?: SortSpec;
}

export function applyBookQuery(rows: DashboardRow[], query: BookQuery, today: string): DashboardRow[] {
  const q = (query.q ?? '').trim().toLowerCase();
  const filters = Object.entries(query.filters ?? {}).filter(([, f]) => isFilterActive(f));
  let out = rows.filter((r) => {
    if (q && !r.name.toLowerCase().includes(q) && !(r.sortName ?? '').toLowerCase().includes(q)
      && !r.emails.some((e) => e?.toLowerCase().includes(q))) return false;
    for (const [key, f] of filters) {
      if (!matchesFilter(cellValue(r, key, today), f, today)) return false;
    }
    return true;
  });
  const sort = query.sort;
  if (sort) {
    out = [...out].sort((a, b) =>
      compareCells(cellValue(a, sort.key, today), cellValue(b, sort.key, today), sort.dir)
      || compareCells(a.sortName ?? a.name, b.sortName ?? b.name, 'asc'));
  }
  return out;
}

/** Distinct value labels for a values-filter list, with counts, in display order. */
export function distinctValues(rows: DashboardRow[], key: string, today: string): Array<{ label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const l = valueLabel(cellValue(r, key, today));
    counts.set(l, (counts.get(l) ?? 0) + 1);
  }
  const order: Record<ComplianceState, number> = { outstanding: 0, pass: 1, unknown: 2 };
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => {
      if (a.label === BLANK) return 1;
      if (b.label === BLANK) return -1;
      if (key.startsWith('c.')) return (order[a.label as ComplianceState] ?? 9) - (order[b.label as ComplianceState] ?? 9);
      return a.label.localeCompare(b.label, 'en-AU', { numeric: true });
    });
}

export function sumColumn(rows: DashboardRow[], key: string, today: string): number {
  let s = 0;
  for (const r of rows) {
    const v = cellValue(r, key, today);
    if (typeof v === 'number') s += v;
  }
  return s;
}

/** CSV of exactly what's on screen (visible columns, filtered + sorted rows). */
export function toCsv(rows: DashboardRow[], columns: BookColumn[], today: string): string {
  const esc = (v: CellValue) => {
    if (v == null) return '';
    const s = String(v);
    // Neutralise spreadsheet formula injection from free-text fields.
    const safe = /^[=+\-@\t\r]/.test(s) && typeof v === 'string' ? `'${s}` : s;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const head = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(cellValue(r, c.key, today))).join(','));
  return [head, ...body].join('\n');
}
